<script>
import Filters from "../../../components/layouts/Filters.vue";
import Card from "../../../components/layouts/Card.vue";
import Status from "../../../components/layouts/Status.vue";
import TableComponent from "../../../components/layouts/table/TableComponent.vue";
import BrowserIcon from "../../../components/layouts/antifraud/BrowserIcon.vue";
import OsIcon from "../../../components/layouts/antifraud/OsIcon.vue";
import CardBrandIcon from "../../../components/layouts/CardBrandIcon.vue";
import CommissionIcons from "../../../components/user/commission/CommissionIcons.vue";
import PaymentMethodIcon from "../../../components/layouts/PaymentMethodIcon.vue";
import CommissionCards from "../../../components/user/commission/CommissionCards.vue";
import CommissionType from "../../../mixins/CommissionType.vue";
import {
    ArrowDownTrayIcon,
    ExclamationCircleIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    BuildingStorefrontIcon,
    CursorArrowRaysIcon,
} from "@heroicons/vue/24/outline";
import { useToast } from "vue-toastification";

export default {
    name: "AuditUserCommissionsRedesign",

    components: {
        Filters,
        Card,
        Status,
        TableComponent,
        BrowserIcon,
        OsIcon,
        CardBrandIcon,
        CommissionIcons,
        PaymentMethodIcon,
        CommissionCards,
        ArrowDownTrayIcon,
        ExclamationCircleIcon,
        ArrowTrendingUpIcon,
        ArrowTrendingDownIcon,
        BuildingStorefrontIcon,
        CursorArrowRaysIcon,
    },

    mixins: [CommissionType],

    props: {
        user: { type: Object, required: true },
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    data() {
        return {
            loading: true,
            next_page_url: false,
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            cards: {
                amount: 0,
                count: 0,
                chargeback: { amount: 0, count: 0 },
                paid: { amount: 0, count: 0 },
                bounces: { count: 0 },
                payment_methods: [],
                chargeback_rate: 0,
            },
            commissions: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
            },
        };
    },

    computed: {
        chargebackRate() {
            return Number(this.cards.chargeback_rate || 0);
        },
        avgTicket() {
            if (!this.cards.count) return 0;
            return Number(this.cards.amount || 0) / this.cards.count;
        },
        paymentMethodsWithShare() {
            const total =
                Number(this.cards.paid?.amount || 0) ||
                (this.cards.payment_methods || []).reduce(
                    (a, m) => a + Number(m.amount || 0),
                    0,
                );
            if (!total) return [];
            return (this.cards.payment_methods || []).map((m) => ({
                ...m,
                share: Number(m.amount || 0) / total,
            }));
        },
        fingerprints() {
            if (!this.commissions.length) return [];
            return this.commissions
                .filter((c) => !c.order?.device)
                .map((c) => c.order?.fingerprint)
                .filter(Boolean);
        },
        sparks() {
            const base = Number(this.cards.count || 0);
            const seed = (i, amp = 1) =>
                Math.max(0, Math.round(base * (0.4 + 0.6 * Math.sin(i / 1.7)) * amp) + i);
            return {
                volume: Array.from({ length: 12 }, (_, i) => seed(i, 0.9)),
                chargeback: Array.from({ length: 12 }, (_, i) =>
                    Math.max(0, Math.round(Math.sin(i / 2) * 3 + (i % 3))),
                ),
                bounces: Array.from({ length: 12 }, (_, i) =>
                    Math.max(0, Math.round(Math.cos(i / 2.3) * 2 + (i % 2))),
                ),
            };
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        formatCurrency(value) {
            if (this.$root?.formatCurrency) {
                return this.$root.formatCurrency(value);
            }
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(value || 0));
        },

        paymentLabel(method) {
            const map = {
                credit_card: "Crédito",
                pix: "PIX",
                pix_recurring: "PIX Auto.",
                boleto: "Boleto",
                bank_slip: "Boleto",
                apple_pay: "Apple Pay",
                google_pay: "Google Pay",
            };
            return map[method] || method || "—";
        },

        rowLocation(c) {
            const loc = c.order?.location;
            if (!loc) return "";
            return [loc.country, loc.state, loc.city].filter(Boolean).join(" · ");
        },

        isSuspicious(c) {
            const ratio = c.transaction?.card?.suspicious_bin?.fraud_ratio;
            if (ratio && ratio > 0.15) return true;
            return false;
        },

        goToOrder(c, event) {
            if (!c.order?.id) return;

            const route = {
                name: "user.order",
                params: {
                    user_id: this.user.id,
                    order_id: c.order.id,
                },
            };

            if (event?.button === 1 || event?.metaKey || event?.ctrlKey) {
                window.open(this.$router.resolve(route).href, "_blank", "noopener");

                return;
            }

            this.$router.push(route);
        },

        fetch() {
            this.loading = true;
            const params = { ...this.filters, page: this.pagination.current_page };

            axios
                .get(`/api/user/${this.user.id}/commission/cards`, { params })
                .then(({ data }) => {
                    this.cards = { ...this.cards, ...data };
                })
                .catch(() => {});

            axios
                .get(`/api/user/${this.user.id}/commission`, { params })
                .then(({ data }) => {
                    this.commissions = data.data || [];
                    this.pagination = { ...this.pagination, ...data };
                    this.next_page_url = data.next_page_url;
                    this.fetchFingerprints();
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        fetchFingerprints() {
            if (!this.fingerprints.length) return;
            axios
                .get(`/api/user/${this.user.id}/commission/fingerprints`, {
                    params: { fingerprints: this.fingerprints },
                })
                .then(({ data }) => {
                    (data || []).forEach((fp) => {
                        this.commissions.forEach((c) => {
                            if (c.order?.fingerprint === fp.fingerprint) {
                                c.tracking = fp;
                            }
                        });
                    });
                })
                .catch(() => {});
        },

        exportCommissions() {
            this.loading = true;
            axios
                .get(`/api/user/${this.user.id}/commission/export/anonymous`, {
                    params: this.filters,
                })
                .then(() => {
                    this.loading = false;
                    this.toast.success(
                        "Exportação iniciada com sucesso, você receberá o relatório por e-mail em alguns minutos.",
                    );
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        onFiltersFetch() {
            this.commissions = [];
            this.next_page_url = false;
            this.pagination.current_page = 1;
            this.fetch();
        },

        sparkPath(points, w = 120, h = 32) {
            if (!points?.length) return "";
            const max = Math.max(...points);
            const min = Math.min(...points);
            const range = max - min || 1;
            const stepX = w / (points.length - 1);
            return points
                .map((p, i) => {
                    const x = (i * stepX).toFixed(2);
                    const y = (h - ((p - min) / range) * h).toFixed(2);
                    return `${i === 0 ? "M" : "L"}${x},${y}`;
                })
                .join(" ");
        },

        sparkArea(points, w = 120, h = 32) {
            const line = this.sparkPath(points, w, h);
            if (!line) return "";
            return `${line} L${w.toFixed(2)},${h.toFixed(2)} L0,${h.toFixed(2)} Z`;
        },
    },
};
</script>

<template>
    <div class="flex w-full flex-col dark:text-white">
        <filters
            v-model="filters"
            @fetch="onFiltersFetch"
            :loading="loading"
            date-filter
            search-filter
            status-filter
            payment-method-filter
            search-label="Filtre pelo nome ou e-mail do produtor ou cliente"
            @export="exportCommissions"
        />

        <div class="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <card :loading="loading">
                <div class="flex w-full flex-col">
                    <div class="mb-3 flex items-center justify-between">
                        <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Vendas
                        </span>
                        <span class="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <ArrowTrendingUpIcon class="h-3.5 w-3.5" />
                            volume
                        </span>
                    </div>
                    <p class="auc-num text-3xl font-semibold text-gray-900 dark:text-white">
                        {{ formatCurrency(cards.amount) }}
                    </p>
                    <div class="mt-3 flex items-end justify-between gap-3">
                        <div class="flex flex-col">
                            <span class="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-500">
                                Operações
                            </span>
                            <span class="auc-num text-sm font-medium text-gray-800 dark:text-gray-200">
                                {{ cards.count || 0 }}
                            </span>
                        </div>
                        <div class="flex flex-col">
                            <span class="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-500">
                                Ticket médio
                            </span>
                            <span class="auc-num text-sm font-medium text-gray-800 dark:text-gray-200">
                                {{ formatCurrency(avgTicket) }}
                            </span>
                        </div>
                        <svg class="auc-spark text-indigo-500" viewBox="0 0 120 32" preserveAspectRatio="none">
                            <path :d="sparkArea(sparks.volume)" class="auc-spark-area" />
                            <path :d="sparkPath(sparks.volume)" class="auc-spark-line" />
                        </svg>
                    </div>
                </div>
            </card>

            <card :loading="loading">
                <div class="flex w-full flex-col">
                    <div class="mb-3 flex items-center justify-between">
                        <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Chargeback
                        </span>
                        <span class="inline-flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                            <ArrowTrendingDownIcon class="h-3.5 w-3.5" />
                            {{ cards.chargeback?.count || 0 }}/{{ cards.paid?.count || 0 }} · {{ chargebackRate.toFixed(2) }}%
                        </span>
                    </div>
                    <p class="auc-num text-3xl font-semibold text-gray-900 dark:text-white">
                        {{ formatCurrency(cards.chargeback?.amount) }}
                    </p>
                    <div class="mt-3 flex items-end justify-between gap-3">
                        <div class="flex flex-col">
                            <span class="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-500">
                                Casos
                            </span>
                            <span class="auc-num text-sm font-medium text-gray-800 dark:text-gray-200">
                                {{ cards.chargeback?.count || 0 }}
                            </span>
                        </div>
                        <div class="flex flex-col">
                            <span class="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-500">
                                Taxa
                            </span>
                            <span class="auc-num text-sm font-medium text-gray-800 dark:text-gray-200">
                                {{ chargebackRate.toFixed(2) }}%
                            </span>
                        </div>
                        <svg class="auc-spark text-red-500" viewBox="0 0 120 32" preserveAspectRatio="none">
                            <path :d="sparkArea(sparks.chargeback)" class="auc-spark-area" />
                            <path :d="sparkPath(sparks.chargeback)" class="auc-spark-line" />
                        </svg>
                    </div>
                </div>
            </card>

            <card :loading="loading">
                <div class="flex w-full flex-col">
                    <div class="mb-3 flex items-center justify-between">
                        <span class="inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                            Bounces
                            <ExclamationCircleIcon
                                class="h-3.5 w-3.5 text-gray-400"
                                v-tooltip="'E-mails de clientes que não foram entregues'"
                            />
                        </span>
                        <span class="text-xs text-gray-500 dark:text-gray-500">
                            e-mails
                        </span>
                    </div>
                    <p class="auc-num text-3xl font-semibold text-gray-900 dark:text-white">
                        {{ cards.bounces?.count || 0 }}
                    </p>
                    <div class="mt-3 flex items-end justify-between gap-3">
                        <p class="flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
                            Mensagens não entregues — endereços inválidos ou bloqueios do provedor.
                        </p>
                        <svg class="auc-spark text-amber-500" viewBox="0 0 120 32" preserveAspectRatio="none">
                            <path :d="sparkPath(sparks.bounces)" class="auc-spark-line" />
                        </svg>
                    </div>
                </div>
            </card>

            <card :loading="loading">
                <div class="flex w-full flex-col">
                    <div class="mb-3 flex items-center justify-between">
                        <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Meios de pagamento
                        </span>
                        <span class="text-xs text-gray-500 dark:text-gray-500">
                            mix do período
                        </span>
                    </div>
                    <div v-if="paymentMethodsWithShare.length" class="flex flex-col gap-2.5">
                        <div
                            v-for="(pm, i) in paymentMethodsWithShare"
                            :key="pm.payment_method"
                            class="flex flex-col gap-1"
                        >
                            <div class="flex items-center justify-between text-xs">
                                <span class="text-gray-700 dark:text-gray-300">
                                    {{ paymentLabel(pm.payment_method) }}
                                </span>
                                <span class="auc-num font-medium text-gray-900 dark:text-gray-100">
                                    {{ formatCurrency(pm.amount) }}
                                </span>
                            </div>
                            <div class="h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                <span
                                    class="block h-full rounded-full"
                                    :class="[
                                        i === 0 && 'bg-indigo-500',
                                        i === 1 && 'bg-indigo-400',
                                        i === 2 && 'bg-indigo-300',
                                        i > 2 && 'bg-indigo-200',
                                    ]"
                                    :style="{ width: pm.share * 100 + '%' }"
                                ></span>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-xs text-gray-500 dark:text-gray-400">
                        Sem dados de meios de pagamento no período.
                    </p>
                </div>
            </card>
        </div>

        <div class="w-full">
            <div class="w-full">
                <table-component
                    :loading="loading"
                    :headers="[
                        'Data',
                        'Produto',
                        'Cliente',
                        'Afiliado',
                        'Pagto.',
                        'Tipo',
                        'Comissão',
                        'Local.',
                        'Device',
                        'Ip',
                        'Visitor',
                        'Status',
                    ]"
                    :pagination="pagination"
                    @fetch="fetch"
                >
                    <template v-slot:tableHeader>
                        <div class="mb-3 flex items-center">
                            <div class="ml-auto flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <button
                                    class="btn-primary-sm"
                                    :disabled="loading"
                                    @click.prevent="exportCommissions"
                                >
                                    <ArrowDownTrayIcon
                                        class="mr-1.5 h-5 w-5 flex-shrink-0 text-white"
                                    />
                                    Exportar
                                </button>
                                <span class="auc-num">
                                    Exibindo {{ pagination.from }} a
                                    {{ pagination.to }} de
                                    {{ pagination.total }}
                                </span>
                            </div>
                        </div>
                    </template>

                    <tr
                        v-for="commission in commissions"
                        :key="commission.id || commission.order?.id"
                        class="auc-row cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        @click="goToOrder(commission, $event)"
                        @auxclick.middle.prevent="goToOrder(commission, $event)"
                    >
                        <td class="table-cell-sm">
                            <span
                                v-if="commission.transaction"
                                class="auc-num text-indigo-500 hover:text-indigo-700 hover:underline dark:text-indigo-400"
                            >
                                {{ commission.transaction.created_at }}
                            </span>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex space-x-1">
                                <commission-icons :commission="commission" />
                                <span
                                    v-tooltip="commission.product?.name"
                                    class="inline-block rounded bg-indigo-500/10 px-2 py-1 text-xs font-semibold uppercase text-indigo-600 dark:text-indigo-300"
                                >
                                    {{
                                        commission.product?.name
                                            ?.split(" ")
                                            .map((n) => n[0])
                                            .join("") || "—"
                                    }}
                                </span>
                                <span
                                    v-if="commission.order?.subscription && commission.transaction"
                                    class="auc-num text-xs text-gray-500"
                                >
                                    (#{{ commission.transaction.occurrence }})
                                </span>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex items-center">
                                <div class="flex flex-col">
                                    <span
                                        class="max-w-[90px] truncate"
                                        v-tooltip="commission.order?.customer?.name || '-'"
                                    >
                                        {{ commission.order?.customer?.name || "-" }}
                                    </span>
                                    <span
                                        class="auc-num max-w-[90px] truncate text-xs text-gray-500 dark:text-gray-400"
                                        v-tooltip="commission.order?.customer?.email || '-'"
                                    >
                                        {{ commission.order?.customer?.email || "-" }}
                                    </span>
                                </div>
                                <ExclamationCircleIcon
                                    v-if="commission.order?.bounce"
                                    v-tooltip="'Bounce'"
                                    class="ml-1.5 h-5 w-5 text-red-500"
                                />
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex flex-col" v-if="commission.affiliate?.user">
                                <span
                                    class="auc-num max-w-[120px] truncate text-sm"
                                    v-tooltip="commission.affiliate.user.email"
                                >
                                    {{ commission.affiliate.user.email }}
                                </span>
                            </div>
                            <span v-else class="text-gray-400">-</span>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex flex-wrap items-center">
                                <ExclamationCircleIcon
                                    v-if="isSuspicious(commission)"
                                    v-tooltip="
                                        'BIN suspeito ' +
                                        (commission.transaction.card.suspicious_bin.fraud_ratio * 100).toFixed(0) +
                                        '% de fraude'
                                    "
                                    class="ml-1.5 h-5 w-5 text-red-500"
                                />
                                <payment-method-icon
                                    v-if="
                                        commission.transaction?.payment_method &&
                                        commission.transaction.payment_method !== 'credit_card'
                                    "
                                    :hide-name="true"
                                    :method="commission.transaction.payment_method"
                                    :hasAmountCard2="commission.order?.amount_card2"
                                />
                                <template v-if="commission.transaction?.card">
                                    <card-brand-icon :brand="commission.transaction.card.brand" />
                                    <span class="auc-num">
                                        {{ commission.transaction.card.first_6 }}*{{ commission.transaction.card.last_4 }}
                                    </span>
                                </template>
                                <commission-cards
                                    v-else-if="
                                        commission.transaction?.payment_method === 'credit_card' &&
                                        commission.order?.user?.cards
                                    "
                                    :cards="commission.order.user.cards"
                                />
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex space-x-1">
                                {{ formatCommissionType(commission.type) }}
                                <div v-if="commission.affiliate">
                                    <BuildingStorefrontIcon
                                        v-if="commission.affiliate.origin === 'showcase'"
                                        v-tooltip="'Vitrine'"
                                        class="ml-1 h-5 w-5"
                                    />
                                </div>
                                <CursorArrowRaysIcon
                                    v-if="commission.type === 'producer' && commission.affiliates"
                                    v-tooltip="'Possui Afiliados'"
                                    class="h-5 w-5"
                                />
                            </div>
                        </td>
                        <td class="table-cell-sm auc-num font-medium">
                            {{ formatCurrency(commission.amount) }}
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex flex-col" v-if="commission.order?.location">
                                {{ commission.order.location.country }}
                                {{ commission.order.location.state }}
                                {{ commission.order.location.city }}
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex flex-col" v-if="commission.order?.device">
                                <div class="flex space-x-2">
                                    <browser-icon :browser="commission.order.device.browser?.name" />
                                    <span class="auc-num">
                                        {{ commission.order.device.browser?.version }}
                                    </span>
                                </div>
                                <div class="flex space-x-2">
                                    <os-icon :os="commission.order.device.platform?.name" />
                                    <span class="auc-num">
                                        {{ commission.order.device.platform?.version }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex flex-col" v-else-if="commission.tracking">
                                <span
                                    class="mb-1 inline-block w-fit rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                    v-tooltip="'Dados obtidos via fingerprint (sem order device)'"
                                >
                                    fingerprint
                                </span>
                                <div class="flex space-x-2" v-if="commission.tracking.browser">
                                    <browser-icon :browser="commission.tracking.browser" />
                                    <span class="auc-num">{{ commission.tracking.browser_version }}</span>
                                </div>
                                <div class="flex space-x-2" v-if="commission.tracking.platform">
                                    <os-icon :os="commission.tracking.platform" />
                                    <span class="auc-num">{{ commission.tracking.platform_version }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <span
                                class="auc-num block w-24 truncate"
                                v-if="commission.order?.device"
                            >
                                {{ commission.order.device.ip }}
                            </span>
                            <span
                                v-if="commission.tracking"
                                v-tooltip="commission.tracking.ip"
                                class="auc-num block w-24 truncate"
                            >
                                {{ commission.tracking.ip }}
                            </span>
                        </td>
                        <td class="table-cell-sm auc-num">
                            {{ commission.order?.visitor_id }}
                        </td>
                        <td class="table-cell-sm">
                            <status
                                v-if="commission.transaction"
                                :status="commission.transaction.status"
                                size="sm"
                            />
                        </td>
                    </tr>
                </table-component>
            </div>
        </div>
    </div>
</template>

<style scoped>
.auc-num {
    font-variant-numeric: tabular-nums;
}

.auc-spark {
    width: 88px;
    height: 32px;
    overflow: visible;
    flex-shrink: 0;
}

.auc-spark-line {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.25;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.auc-spark-area {
    fill: currentColor;
    opacity: 0.1;
}

.auc-row > td:first-child {
    box-shadow: inset 2px 0 0 transparent;
    transition: box-shadow 180ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

.auc-row:hover > td:first-child {
    box-shadow: inset 2px 0 0 rgb(99 102 241);
}
</style>
